import Layout from '@/components/Layout'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import '../reports/style.css'
import api from '@/services/api'
import { API_ENDPOINTS } from '@/config/api'
import { formatAmount } from '@/utils/constants'
import Swal from 'sweetalert2'

const ROLE_HIERARCHY = ["OWNER", "COMPANY", "SUPERADMIN", "ADMIN", "SUPERMASTER", "MASTER", "USER"];

/**
 * CreateAccount Component
 * 
 * Page for creating user accounts with information, credit, account details, and notes.
 */
function AgentCreate() {
  const { user, role } = useSelector((state) => state.auth);
  const loggedInUserCredit = user?.credit ? parseFloat(user.credit) : 0;
  const isOwner = (role || '').toLowerCase() === 'owner' || (localStorage.getItem('activeRole') || '').toLowerCase() === 'owner';

  const [formData, setFormData] = useState({
    loginName: '',
    password: '',
    repeatPassword: '',
    userStatus: 'ACTIVE',
    betStatus: 'ACTIVE',
    creditLimit: '0',
    userRate: '1',
    selectLevel: '',
    notes: '',
    masterPassword: ''
  })
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Get the actual role from Redux state (user.role contains the hierarchy role like SUPERADMIN, ADMIN, etc.)
    const userRole = user?.role;

    if (userRole) {
      const upperRole = userRole.toUpperCase();
      const roleIndex = ROLE_HIERARCHY.indexOf(upperRole);

      if (roleIndex !== -1) {
        // Filter roles that are strictly below the current user's role
        let allowed = ROLE_HIERARCHY.slice(roleIndex + 1);

        // If owner, remove USER from allowed roles
        if (isOwner) {
          allowed = allowed.filter(r => r !== 'USER');
        }

        setAvailableRoles(allowed);
      } else {
        // Fallback or handle unknown role
        setAvailableRoles(ROLE_HIERARCHY.slice(ROLE_HIERARCHY.indexOf("ADMIN")));
      }
    } else {
      // Fallback default
      setAvailableRoles(["ADMIN", "SUPERMASTER", "MASTER", "USER"]);
    }
  }, [user, isOwner]);

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }
  function calculateScale() {
    const DESIGN_WIDTH = 1200;          // tumhara desktop design
    const screenWidth = window.innerWidth;

    let scale = screenWidth / DESIGN_WIDTH;

    // clamp values (important)
    if (scale > 1) scale = 1;           // desktop
    if (scale < 0.7) scale = 0.7;       // too small phones

    return Number(scale.toFixed(2));
  }



  useEffect(() => {
    const wrapper = document.querySelector('.agent-create-wrapper');
    if (!wrapper) return;

    const applyScale = () => {
      const scale = calculateScale();

      wrapper.style.transform = `scale(${scale})`;
      wrapper.style.transformOrigin = 'top center';

      // width compensate so layout center rahe
      wrapper.style.width = `${100 / scale}%`;
      wrapper.style.marginLeft = `${-(100 / scale - 100) / 2}%`;
    };

    applyScale();
    window.addEventListener('resize', applyScale);

    return () => window.removeEventListener('resize', applyScale);
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.loginName || !formData.password || !formData.repeatPassword) {
      alert('Please fill in all required fields')
      return
    }

    if (formData.password !== formData.repeatPassword) {
      alert('Password and Repeat Password do not match')
      return
    }

    if (!formData.selectLevel) {
      alert('Please select a level')
      return
    }

    if (!formData.masterPassword) {
      alert('Master Password is required')
      return
    }

    // Dynamic Credit Validation
    if (!isOwner) {
      const proposedCredit = parseFloat(formData.creditLimit) || 0;
      if (proposedCredit < 0) {
        alert('Credit limit cannot be negative');
        return;
      }
      if (proposedCredit > loggedInUserCredit) {
        alert(`Credit limit cannot exceed your available credit of ${formatAmount(loggedInUserCredit)}`);
        return;
      }
    }

    setLoading(true)
    try {

      const email = `${formData.loginName.toLowerCase()}@gmail.com`; // Auto-generate email

      // Base payload for both user and staff
      const payload = {
        username: formData.loginName,
        email: email,
        password: formData.password,
        role: formData.selectLevel, // e.g., "MASTER"
        credit: parseFloat(formData.creditLimit) || 0,
        percentage: parseFloat(formData.userRate) || 0,
      };

      // Only add transactionPassword for verification (not for staff creation - it's auto-generated)
      if (formData.selectLevel === "USER") {
        payload.transactionPassword = formData.masterPassword; // For verification
      } else {
        // For staff, transaction password is auto-generated by backend
        payload.transactionPassword = formData.masterPassword; // For verification middleware
      }

      let response;
      if (formData.selectLevel === "USER") {
        response = await api.post(API_ENDPOINTS.USER.CREATE_USER_UNDER_PARENT, payload);
      } else {
        response = await api.post(API_ENDPOINTS.USER.CREATE_STAFF, payload);
      }

      if (response.data) {
        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: formData.selectLevel === "USER"
            ? 'User account created successfully'
            : 'Staff account created successfully',
          timer: 2000
        });

        // Reset form
        setFormData({
          loginName: '',
          password: '',
          repeatPassword: '',
          userStatus: 'ACTIVE',
          betStatus: 'ACTIVE',
          creditLimit: '0',
          userRate: '1',
          selectLevel: '',
          notes: '',
          masterPassword: ''
        })
      }

    } catch (error) {
      console.error('Error creating account:', error)
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to create account';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg
      });
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    // Reset form
    setFormData({
      loginName: '',
      password: '',
      repeatPassword: '',
      userStatus: 'ACTIVE',
      betStatus: 'ACTIVE',
      creditLimit: '0',
      userRate: '1',
      selectLevel: '',
      notes: '',
      masterPassword: ''
    })
  }

  const isDesktop = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 1024;
  };

  const [desktop, setDesktop] = useState(() => isDesktop());

  useEffect(() => {
    const onResize = () => {
      setDesktop(isDesktop());
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);



  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >

          <div className="agent-create-wrapper">
            <form onSubmit={handleSubmit}>

              <div>
                <div
                  className='grid gap-4 mb-4 grid-cols-4'
                >


                  {/* Information Section */}
                  <div
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.07)" }}
                    className="shadow-sm px-4 py-4 flex flex-col h-full"
                  >
                    <div
                      style={{ color: "#cae0e8", fontSize: "20px" }}
                      className="font-semibold mb-2"
                    >
                      Information
                    </div>
                    <div className="w-full h-px bg-white mb-4"></div>

                    <div className="space-y-4 flex-1">
                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-1">Login Name</label>
                        <input
                          type="text"
                          name="loginName"
                          value={formData.loginName}
                          onChange={handleInputChange}
                          placeholder="Login Id"
                          className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-1">Password</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Password"
                          className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-1">Repeat Password</label>
                        <input
                          type="password"
                          name="repeatPassword"
                          value={formData.repeatPassword}
                          onChange={handleInputChange}
                          placeholder="Confirm Password"
                          className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-2">User Status</label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="userStatus"
                              value="ACTIVE"
                              checked={formData.userStatus === 'ACTIVE'}
                              onChange={handleInputChange}
                              className="w-4 h-4"
                              style={{ accentColor: "#3cd247" }}
                            />
                            <span className="text-white text-sm">ACTIVE</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="userStatus"
                              value="INACTIVE"
                              checked={formData.userStatus === 'INACTIVE'}
                              onChange={handleInputChange}
                              className="w-4 h-4"
                              style={{ accentColor: "#3cd247" }}
                            />
                            <span className="text-white text-sm">INACTIVE</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-2">Bet Status</label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="betStatus"
                              value="ACTIVE"
                              checked={formData.betStatus === 'ACTIVE'}
                              onChange={handleInputChange}
                              className="w-4 h-4"
                              style={{ accentColor: "#3cd247" }}
                            />
                            <span className="text-white text-sm">ACTIVE</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="betStatus"
                              value="INACTIVE"
                              checked={formData.betStatus === 'INACTIVE'}
                              onChange={handleInputChange}
                              className="w-4 h-4"
                              style={{ accentColor: "#3cd247" }}
                            />
                            <span className="text-white text-sm">INACTIVE</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credit Section */}
                  <div
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.07)" }}
                    className="shadow-sm px-4 py-4 self-start"
                  >
                    <div
                      style={{ color: "#cae0e8", fontSize: "20px" }}
                      className="font-semibold mb-2"
                    >
                      Credit
                    </div>
                    <div className="w-full h-px bg-white mb-4"></div>

                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-1">Credit Limit</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            name="creditLimit"
                            value={formData.creditLimit}
                            onChange={handleInputChange}
                            min="0"
                            max={isOwner ? undefined : loggedInUserCredit}
                            className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
                          />
                          <div className="text-white text-xs">
                            <div>&gt;=0</div>
                            <div>
                              {isOwner ? '(No Limit)' : `<= ${formatAmount(loggedInUserCredit)}`}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-1">User Rate</label>
                        <input
                          type="number"
                          name="userRate"
                          value={formData.userRate}
                          onChange={handleInputChange}
                          step="0.01"
                          className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Account Details Section */}
                  <div
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.07)" }}
                    className="shadow-sm px-4 py-4 self-start"
                  >
                    <div
                      style={{ color: "#cae0e8", fontSize: "20px" }}
                      className="font-semibold mb-2"
                    >
                      Account Details
                    </div>
                    <div className="w-full h-px bg-white mb-4"></div>

                    <div className="space-y-4">
                      <div className="flex flex-col">
                        <label className="text-white text-sm mb-1">Select Level</label>
                        <select
                          name="selectLevel"
                          value={formData.selectLevel}
                          onChange={handleInputChange}
                          className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Level</option>
                          {availableRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.07)" }}
                    className="shadow-sm px-4 py-4 self-start"
                  >
                    <div
                      style={{ color: "#cae0e8", fontSize: "20px" }}
                      className="font-semibold mb-2"
                    >
                      Notes
                    </div>
                    <div className="w-full h-px bg-white mb-4"></div>

                    <div className="flex flex-col ">
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Notes..."
                        className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-h-[150px]  resize-none"
                      />
                    </div>
                  </div>
                </div>
                {/* Master Password and Action Buttons */}
                <div className="flex flex-col gap-2 justify-end items-end mt-4 mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-sm">Master Password</label>
                    <input
                      type="password"
                      name="masterPassword"
                      value={formData.masterPassword}
                      onChange={handleInputChange}
                      placeholder="Master Password"
                      className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="flex gap-2 mb-4 mt-4">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-6 py-2 text-[#3cd247] font-medium rounded-sm hover:bg-[rgba(60,210,71,0.1)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-[#333d79] text-white font-medium rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>



            </form>
          </div>
        </div>

      </div>
    </Layout>
  )
}

export default AgentCreate;
